// UUIDv7 generator — time-ordered IDs for ProseMirror block nodes.
//
// Picked over UUIDv4 so that when the IDs eventually back a CRDT or
// land in Postgres, b-tree indexes stay locality-friendly: rows
// created near each other in time sort near each other on disk.
//
// Spec: draft-ietf-uuidrev-rfc4122bis (UUID Version 7). Layout:
//   48 bits  unix_ts_ms
//    4 bits  version (= 0x7)
//   12 bits  rand_a
//    2 bits  variant (= 0b10)
//   62 bits  rand_b
//
// Runtime: works in both the renderer (browser globalThis.crypto) and
// Node (globalThis.crypto since 19; we're on 20). No external dep.

function getRandomBytes(n) {
  const out = new Uint8Array(n);
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.getRandomValues) {
    globalThis.crypto.getRandomValues(out);
    return out;
  }
  // Last-resort fallback — vitest+jsdom should always provide crypto.
  for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
  return out;
}

function toHex(byte) {
  return byte.toString(16).padStart(2, '0');
}

export function uuidv7() {
  const ms = Date.now();
  // 48-bit timestamp split into 32+16 bits.
  const tsHi = Math.floor(ms / 0x10000); // top 32
  const tsLo = ms & 0xffff; // bottom 16

  const rand = getRandomBytes(10);

  // rand[0..1] supplies the 12-bit rand_a (low 12 bits of bytes 0..1).
  // We then OR in the version nibble (0x7) at the high nibble of byte 0
  // of this 16-bit field.
  const randA = ((rand[0] << 8) | rand[1]) & 0x0fff;
  const verRandA = 0x7000 | randA;

  // Bytes 2..9 supply the 64-bit rand_b region. We set the top two bits
  // of byte 2 to 0b10 to mark the RFC 4122 variant.
  rand[2] = (rand[2] & 0x3f) | 0x80;

  const tsHiHex = tsHi.toString(16).padStart(8, '0');
  const tsLoHex = tsLo.toString(16).padStart(4, '0');
  const verRandAHex = verRandA.toString(16).padStart(4, '0');
  const variantHex = `${toHex(rand[2])}${toHex(rand[3])}`;
  const tailHex = Array.from(rand.subarray(4, 10), toHex).join('');

  return `${tsHiHex}-${tsLoHex}-${verRandAHex}-${variantHex}-${tailHex}`;
}

// Cheap predicate used by appendTransaction backfill and tests. Doesn't
// validate the version bits — any 8-4-4-4-12 hex string passes. That's
// enough to distinguish "stamped" from "missing"; full validation would
// reject legacy IDs we want to keep accepting on import.
const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value) {
  return typeof value === 'string' && ID_RE.test(value);
}
